import React from 'react';
import UseInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SalesRecording } from '@marageti/z4-sdk/lib/travel';
import {
  ChipOption,
  ChipSelectList,
  DebouncedSearchInput,
  ZSpinner,
} from '@marageti/z4-lib';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { DatePicker } from '@mui/x-date-pickers';
import SalesTable from './sales-table';
import PartnerSelector from '../../../components/partner-selector';

const today = new Date();
type Option = {
  title: string;
  value: string;
};

type Props = {
  chipStatusOptions: ChipOption[];
  chipTypeOptions: ChipOption[];
  error: ApiError | undefined;
  handleSearchInputChange: (value: string) => void;
  hasMore: boolean;
  loading: boolean;
  loadMore: () => void;
  onChangeEndDay: (value: Date | null) => void,
  onChangeOrderBy: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangePartner: (value: string) => void;
  onChangeSortBy: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeStartDay: (value: Date | null) => void,
  onChipsChange: (value: string[], type: 'status' | 'salesRecordingType') => void;
  orderBy: string;
  orderByOptions: Option[];
  sales: SalesRecording[],
  searchTerm: string;
  selectedEndDay: Date | null,
  selectedPartner: string,
  selectedStartDay: Date | null,
  selectedStatusChips: string[];
  selectedTypeChips: string[];
  sortBy: string;
  sortByOptions: Option[];
};

const AllSalesView = ({
  chipStatusOptions,
  chipTypeOptions,
  error,
  handleSearchInputChange,
  hasMore,
  loading,
  loadMore,
  onChangeEndDay,
  onChangeOrderBy,
  onChangePartner,
  onChangeSortBy,
  onChangeStartDay,
  onChipsChange,
  orderBy,
  orderByOptions,
  sales,
  searchTerm,
  selectedEndDay,
  selectedPartner,
  selectedStartDay,
  selectedStatusChips,
  selectedTypeChips,
  sortBy,
  sortByOptions,
}: Props) => {
  const [sentryRef] = UseInfiniteScroll({
    disabled: !!error,
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMore,
  });
  const minDate = new Date('2008-01-01T00:00:00');
  return (
    <Container maxWidth="xl">
      <Box
        sx={{
          pt: { desktop: 4.5, mobile: 6.5 },
          pb: 20,
        }}
      >
        <Stack spacing={3} mb={5} useFlexGap>
          <Typography variant="h2">All Sales</Typography>
          <Grid
            container
            columnSpacing={{
              mobile: 0, desktop: 2,
            }}
          >
            <Grid
              item
              mobile={12}
              desktop={3}
            >
              <DebouncedSearchInput
                defaultValue={searchTerm}
                name="fullText"
                onChange={handleSearchInputChange}
              />
            </Grid>
            <Grid
              item
              mobile={12}
              desktop={3}
            >
              <PartnerSelector
                key="partner-select"
                label="Partner"
                name="partner-input"
                value={selectedPartner}
                onChange={(e, v) => onChangePartner(v)}
              />
            </Grid>
            <Grid
              item
              mobile={12}
              desktop={6}
            >
              <Stack direction="row" alignItems="center">
                <DatePicker
                  label="Start Date"
                  maxDate={selectedEndDay || today}
                  onChange={onChangeStartDay}
                  value={selectedStartDay}
                  views={['year', 'month', 'day']}
                  format="MM/dd/yy"
                  minDate={minDate}
                  slotProps={{
                    textField: {
                      sx: {
                        maxWidth: { desktop: '304px' },
                        width: '100%',
                      },
                    },
                  }}
                />
                <Typography mx={1}>to</Typography>
                <DatePicker
                  label="End Date"
                  maxDate={today}
                  onChange={onChangeEndDay}
                  value={selectedEndDay}
                  views={['year', 'month', 'day']}
                  format="MM/dd/yy"
                  minDate={selectedStartDay || minDate}
                  slotProps={{
                    textField: {
                      sx: {
                        maxWidth: { desktop: '304px' },
                        width: '100%',
                      },
                    },
                  }}
                />
              </Stack>
            </Grid>
            <Grid
              item
              mobile={6}
              desktop={3}
            >
              <TextField
                fullWidth
                select
                disabled={!!searchTerm.length}
                key="sory-by-select"
                label="Sort By"
                name="sortBy"
                onChange={onChangeSortBy}
                value={sortBy}
              >
                {sortByOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid
              item
              mobile={6}
              desktop={3}
            >
              <TextField
                fullWidth
                select
                key="order-by-select"
                label="Order By"
                name="orderBy"
                onChange={onChangeOrderBy}
                value={orderBy}
              >
                {orderByOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.title}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Stack
            direction={{
              mobile: 'column', desktop: 'row',
            }}
            spacing={{
              mobile: 2, desktop: 4,
            }}
          >
            <ChipSelectList
              label="Sales Type"
              chipOptions={chipTypeOptions}
              selectedChips={selectedTypeChips}
              onChange={(chips) => onChipsChange(chips, 'salesRecordingType')}
            />
            <ChipSelectList
              label="Status"
              chipOptions={chipStatusOptions}
              selectedChips={selectedStatusChips}
              onChange={(chips) => onChipsChange(chips, 'status')}
            />
          </Stack>
        </Stack>
        {loading ? (
          <Box display="flex" justifyContent="center">
            <ZSpinner />
          </Box>
        ) : null}

        {!loading && sales.length > 0 ? (
          <SalesTable
            sales={sales}
            sentryRef={sentryRef}
            hasMore={hasMore}
          />
        ) : null}

        {!loading && sales.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body" color="primary.light" textAlign="center">
                There are no sales recordings for the selected month.
              </Typography>
            </CardContent>
          </Card>
        ) : null}
      </Box>
    </Container>
  );
};
export default AllSalesView;
